import {useCallback, useContext, useMemo, useState} from "react";
import {useNavigate} from "react-router-dom";
import {ContextAppDispatch, ContextAppState} from "@app/context";
import {useSetMetaMaskAuth, useSetTelegramAuth, useSetUnauthorized, useSetWallet} from "@states/local.ts";
import {retrieveRawInitData} from '@telegram-apps/sdk-react';

export const useAuth = () => {
    const {auth} = useContext(ContextAppState);
    return auth;
}


type LogoutAction = (abortController?: AbortController) => void;

export const useLogout = (): [LogoutAction, boolean, Error | null] => {
    const {auth} = useContext(ContextAppState);
    const appDispatch = useContext(ContextAppDispatch);
    const navigate = useNavigate();
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<Error | null>(null);
    const setUnauthorized = useSetUnauthorized();


    const fn = useCallback(async (abortController?: AbortController) => {
        const controller = abortController ?? new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), 5_000);

        try {
            const token = auth.token;
            appDispatch({type: 'APP_LOGOUT'});

            await fetch(`${import.meta.env.VITE_BASE_URL}/v1/logout`, {
                method: "POST",
                headers: {

                    'Accept': 'application/json',
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                signal: abortController?.signal
            });

        } catch (err: any) {
            if (err.name !== 'AbortError') {
                setError(err);
            }
        } finally {
            setUnauthorized();
            clearTimeout(timeoutId);
            setLoading(false);
        }
    }, [auth.token, appDispatch, navigate]);

    return [fn, loading, error];
};

type MetaMaskAction = (address: string, signature: string, redirect: string, abortController?: AbortController) => void
export const useMetaMask = (): [MetaMaskAction, string | null, boolean, Error | null] => {
    const navigate = useNavigate()
    const appDispatch = useContext(ContextAppDispatch);
    const [token, setToken] = useState<string | null>(null)
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<Error | null>(null);
    const setMetaMaskAuth = useSetMetaMaskAuth();
    const setWallet = useSetWallet();

    const fn = useCallback((address: string, signature: string, redirect: string, abortController?: AbortController) => {
        fetch(`${import.meta.env.VITE_BASE_URL}/v1/auth/metamask`, {
            method: "POST",
            headers: {
                'Accept': 'application/json',
                'Content-Type': 'application/json'
            },
            body: JSON.stringify({
                address,
                signature,
            }),
            signal: abortController?.signal,
        })
            .then(response => {
                if (!response.ok) {
                    setError(Error(`Request submission failed: ${response.status} - ${response.statusText}`))
                    setLoading(false)
                    return;
                }

                response.json().then(data => {
                    setToken(data.token)

                    appDispatch({
                        type: 'APP_LOGIN_METAMASK',
                        user: data.user,
                        token: data.token,
                        wallet: data.wallet,
                    })

                    setMetaMaskAuth(data.user.id, data.token);
                    setWallet(data.wallet.id, data.wallet.address);

                    navigate(redirect)

                })
                setLoading(false)
            })
            .catch(error => {
                if (error.name !== 'AbortError') {
                    setError(error)
                    setLoading(false)
                    return
                }

            })
    }, [appDispatch, navigate, setMetaMaskAuth])

    return [fn, token, loading, error]
}

export const useRawLaunchParams = () => {
    // @ts-ignore
    return useMemo(retrieveRawInitData, []);
}


type TelegramAction = (redirect: string, abortController?: AbortController) => void
export const useTelegram = (): [TelegramAction, string | null, boolean, Error | null] => {

    const dispatch = useContext(ContextAppDispatch);
    const navigate = useNavigate();
    const [token, setToken] = useState<string | null>(null)
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<Error | null>(null);
    const query = useRawLaunchParams();
    const setTelegramAuth = useSetTelegramAuth();
    const setWallet = useSetWallet();

    const fn = useCallback((redirect: string, abortController?: AbortController) => {
        fetch(`${import.meta.env.VITE_BASE_URL}/v1/auth/telegram`, {
            method: "POST",
            headers: {
                'Accept': 'application/json',
                'Content-Type': 'application/json'

            },
            body: JSON.stringify({query}),
            signal: abortController?.signal,
        })
            .then(response => {
                if (!response.ok) {
                    setError(Error(`Request submission failed: ${response.status} - ${response.statusText}`))
                    setLoading(false)
                    return;

                }
                response
                    .json()
                    .then(data => {
                        setToken(data.token)

                        dispatch({
                            type: 'APP_LOGIN_TELEGRAM',
                            user: data.user,
                            token: data.token,
                            telegram: data.telegram,
                            wallet: data.wallet,
                        })

                        setLoading(false)

                        setTelegramAuth(data.user.id, data.token, data.telegram!!.id);
                        setWallet(data.wallet.id, data.wallet.address);

                        navigate(redirect)
                    })

            })
            .catch(error => {
                if (error.name !== 'AbortError') {
                    setError(error)
                    setLoading(false)
                }
            })
    }, [dispatch])

    return [fn, token, loading, error]
}